"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material"
import { Search as SearchIcon, Refresh as RefreshIcon, FilterList as FilterIcon } from "@mui/icons-material"
import PaymentRecoveryService from "../../services/payment-recovery.service"
import useOrganizationParams from "../../hooks/useOrganizationParams"

const FailedPayments = () => {
  const organizationParams = useOrganizationParams()

  // State variables
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "30",
    failureReason: "all",
  })
  const [retryDialogOpen, setRetryDialogOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [failedPayments, setFailedPayments] = useState([])
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })
  const [retryLoading, setRetryLoading] = useState(false)

  // Fetch failed payments from API
  const fetchFailedPayments = async () => {
    setLoading(true)
    setError(null)
    try {
      // Combine search term with other filters
      const apiFilters = {
        ...filters,
        ...organizationParams,
        searchTerm: searchTerm.trim() || undefined,
      }

      const data = await PaymentRecoveryService.getFailedPayments(apiFilters)
      setFailedPayments(data)
    } catch (err) {
      console.error("Error fetching failed payments:", err)
      setError("Failed to load payment data. Please try again.")
      setSnackbar({
        open: true,
        message: "Failed to load payment data. Please try again.",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  // Load data on mount and when the organization picked in the sidebar changes
  useEffect(() => {
    fetchFailedPayments()
  }, [organizationParams])

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleSearch = () => {
    setPage(0)
    fetchFailedPayments()
  }

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch()
    }
  }

  // Handle filters
  const handleFilterOpen = () => {
    setFilterOpen(true)
  }

  const handleFilterClose = () => {
    setFilterOpen(false)
  }

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    })
  }

  const handleApplyFilters = () => {
    setFilterOpen(false)
    setPage(0)
    fetchFailedPayments()
  }

  // Handle refresh
  const handleRefresh = () => {
    fetchFailedPayments()
  }

  // Handle retry payment
  const handleRetryPayment = (payment) => {
    setSelectedPayment(payment)
    setRetryDialogOpen(true)
  }

  const handleRetryDialogClose = () => {
    setRetryDialogOpen(false)
    setSelectedPayment(null)
  }

  const handleConfirmRetry = async () => {
    if (!selectedPayment) return

    setRetryLoading(true)
    try {
      await PaymentRecoveryService.retryPayment(selectedPayment.id, organizationParams)
      setSnackbar({
        open: true,
        message: "Payment retry initiated successfully",
        severity: "success",
      })

      // Refresh the list after a short delay to allow the backend to update
      setTimeout(() => {
        fetchFailedPayments()
      }, 1000)
    } catch (err) {
      console.error("Error retrying payment:", err)
      setSnackbar({
        open: true,
        message: `Failed to retry payment: ${err.response?.data?.message || err.message}`,
        severity: "error",
      })
    } finally {
      setRetryLoading(false)
      setRetryDialogOpen(false)
      setSelectedPayment(null)
    }
  }

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Format status for display
  const formatStatus = (status) => {
    switch (status) {
      case "pending_retry":
        return { label: "Pending Retry", color: "warning" }
      case "in_dunning":
        return { label: "In Dunning", color: "info" }
      case "in_grace_period":
        return { label: "Grace Period", color: "error" }
      case "canceled":
        return { label: "Canceled", color: "default" }
      default:
        return { label: status, color: "default" }
    }
  }

  // Format failure reason for display
  const formatFailureReason = (reason) => {
    switch (reason) {
      case "insufficient_funds":
        return "Insufficient Funds"
      case "card_declined":
        return "Card Declined"
      case "expired_card":
        return "Expired Card"
      case "invalid_cvc":
        return "Invalid CVC"
      case "processing_error":
        return "Processing Error"
      default:
        return reason
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Failed Payments
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage and retry failed subscription payments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <TextField
          placeholder="Search by ID, customer, or subscription"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearch}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={handleFilterOpen} sx={{ mr: 1 }}>
            Filter
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payment ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Failure Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Failure Reason</TableCell>
                <TableCell>Retry Count</TableCell>
                <TableCell>Next Retry</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && failedPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : failedPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No failed payments found
                  </TableCell>
                </TableRow>
              ) : (
                failedPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payment) => {
                  const statusInfo = formatStatus(payment.status)
                  return (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.id}</TableCell>
                      <TableCell>{payment.customerName}</TableCell>
                      <TableCell>
                        ${payment.amount.toFixed(2)} {payment.currency}
                      </TableCell>
                      <TableCell>{new Date(payment.failureDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                      </TableCell>
                      <TableCell>{formatFailureReason(payment.failureReason)}</TableCell>
                      <TableCell>{payment.retryCount}</TableCell>
                      <TableCell>
                        {payment.nextRetryDate ? new Date(payment.nextRetryDate).toLocaleDateString() : "N/A"}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleRetryPayment(payment)}
                          disabled={payment.status === "canceled" || loading}
                        >
                          Retry Now
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={failedPayments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onClose={handleFilterClose}>
        <DialogTitle>Filter Failed Payments</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, width: 400 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending_retry">Pending Retry</MenuItem>
                <MenuItem value="in_dunning">In Dunning</MenuItem>
                <MenuItem value="in_grace_period">Grace Period</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="date-range-filter-label">Date Range</InputLabel>
              <Select
                labelId="date-range-filter-label"
                name="dateRange"
                value={filters.dateRange}
                onChange={handleFilterChange}
                label="Date Range"
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
                <MenuItem value="all">All time</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="failure-reason-filter-label">Failure Reason</InputLabel>
              <Select
                labelId="failure-reason-filter-label"
                name="failureReason"
                value={filters.failureReason}
                onChange={handleFilterChange}
                label="Failure Reason"
              >
                <MenuItem value="all">All Reasons</MenuItem>
                <MenuItem value="insufficient_funds">Insufficient Funds</MenuItem>
                <MenuItem value="card_declined">Card Declined</MenuItem>
                <MenuItem value="expired_card">Expired Card</MenuItem>
                <MenuItem value="invalid_cvc">Invalid CVC</MenuItem>
                <MenuItem value="processing_error">Processing Error</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button onClick={handleApplyFilters} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Payment Dialog */}
      <Dialog open={retryDialogOpen} onClose={handleRetryDialogClose}>
        <DialogTitle>Retry Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to retry the payment for {selectedPayment?.customerName}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Amount: ${selectedPayment?.amount.toFixed(2)} {selectedPayment?.currency}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subscription: {selectedPayment?.subscriptionId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Previous failure reason: {selectedPayment && formatFailureReason(selectedPayment.failureReason)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRetryDialogClose} disabled={retryLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRetry} variant="contained" color="primary" disabled={retryLoading}>
            {retryLoading ? <CircularProgress size={24} /> : "Retry Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default FailedPayments

